import {
  AssetType,
  GasProductionDataType,
  OptimizationParameterValue,
  TargetPowerScheduleConstraint,
  TargetPowerScheduleConstraintType,
} from '@e2m-customer-portal/domains';
import { mustBeGreaterThan, mustBeLessThan, Nullable } from '@e2m-customer-portal/shared';
import { InfoTooltip, NumberInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { Button, CircularProgress } from '@mui/material';
import { equals } from 'ramda';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GasProductionProps {
  data: Nullable<GasProductionDataType>;
  updateValues: (parameters: string) => void;
  isSaving?: boolean;
  readOnly: (parameter?: string) => boolean;
}

export function GasProduction({ data, updateValues, isSaving, readOnly }: GasProductionProps) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const [isValid, setIsValid] = useState(false);
  const inputClass = 'w-full bg-white';

  const [updatedData, setUpdatedData] = useState(data);
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [targetPowerScheduleConstraints, setTargetPowerScheduleConstraints] = useState<
    TargetPowerScheduleConstraintType[] | undefined
  >(data?.target_power_schedule_constraints);

  const updateGasProductionValues = (isValid: boolean) => {
    const updatedItems = Object.fromEntries(changes);
    if (isValid) {
      const mutationBody = JSON.stringify({
        asset_type: AssetType.Biogas,
        gas_production: updatedItems,
      });
      updateValues(mutationBody);
      setChanges(new Map());
    }
  };

  const resetValues = () => {
    setUpdatedData(data);
    setTargetPowerScheduleConstraints(data?.target_power_schedule_constraints);
    setChanges(new Map());
  };

  useEffect(() => {
    if (
      targetPowerScheduleConstraints &&
      !equals(targetPowerScheduleConstraints, updatedData?.target_power_schedule_constraints)
    ) {
      setUpdatedData({ ...updatedData, target_power_schedule_constraints: targetPowerScheduleConstraints });
      setChanges(changes.set('target_power_schedule_constraints', targetPowerScheduleConstraints));
    }
  }, [targetPowerScheduleConstraints]);

  if (!data)
    return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE_GAS_PRODUCTION')}</div>;

  return (
    <div
      className="p-6 m-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 bg-grey-50 border-solid border border-divider rounded-md"
      data-testid="gas-production"
    >
      <h2 className="text-base font-semibold xl:col-span-2">{t('COPT_GAS_PRODUCTION_TITLE')}</h2>
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        {updatedData?.target_power_electric !== undefined && (
          <NumberInput
            value={updatedData.target_power_electric && updatedData.target_power_electric * 1000}
            className={inputClass}
            required
            name="target_power_electric"
            label={t('COPT_GAS_PRODUCTION_TARGET_POWER_ELECTRIC')}
            onChange={(value: number) => {
              const target_power_electric = value != null && !Number.isNaN(value) ? value / 1000 : null;
              setUpdatedData({
                ...updatedData,
                target_power_electric,
              });
              setChanges(changes.set('target_power_electric', target_power_electric));
            }}
            disabled={readOnly('target_power_electric')}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="target_power_electric" objectType="gas_production" />}
          />
        )}
        {updatedData?.conversion_efficiency !== undefined && (
          <NumberInput
            value={updatedData.conversion_efficiency && Math.round(updatedData.conversion_efficiency * 100)}
            className={inputClass}
            required
            name="conversion_efficiency"
            label={t('COPT_GAS_PRODUCTION_CONVERSION_EFFICIENCY')}
            onChange={(value: number) => {
              const conversion_efficiency = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                conversion_efficiency,
              });
              setChanges(changes.set('conversion_efficiency', conversion_efficiency));
            }}
            disabled={readOnly('conversion_efficiency')}
            notNegative
            rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
            endAdornment={<InfoTooltip field="conversion_efficiency" objectType="gas_production" />}
          />
        )}
        <div className="pt-3 flex border-solid border-0 border-t border-divider xl:col-span-2">
          <h4 className="flex items-center m-0 text-sm font-semibold">
            {t('COPT_GAS_PRODUCTION_TARGET_POWER_SCHEDULE_CONSTRAINTS')}
            <InfoTooltip
              text={t('COPT_GAS_PRODUCTION_TARGET_POWER_SCHEDULE_CONSTRAINTS_TOOLTIP')}
              field="target_power_schedule_constraints"
            />
          </h4>
        </div>
        <div className="xl:col-span-2 grid grid-cols-[repeat(auto-fill,_minmax(max(16rem,_calc((100%-3rem)/7)),_1fr))] gap-x-2 gap-y-4">
          {(targetPowerScheduleConstraints as TargetPowerScheduleConstraintType[]).map((constraint) => (
            <TargetPowerScheduleConstraint
              key={constraint.weekday}
              data={constraint}
              updatedData={updatedData}
              targetPowerScheduleConstraints={targetPowerScheduleConstraints as TargetPowerScheduleConstraintType[]}
              setTargetPowerScheduleConstraints={
                setTargetPowerScheduleConstraints as Dispatch<
                  SetStateAction<TargetPowerScheduleConstraintType[] | undefined>
                >
              }
              readOnly={readOnly}
              setIsValid={setIsValid}
            />
          ))}
        </div>
      </ValidatedContainer>
      {!readOnly() && (
        <div className="flex gap-2 justify-between xl:col-span-2">
          <Button color="primary" variant="outlined" className="bg-white" onClick={resetValues}>
            {t('SHARED_CANCEL')}
          </Button>
          <Button
            color="primary"
            disabled={!isValid || isSaving}
            onClick={() => updateGasProductionValues(isValid)}
            startIcon={isSaving && <CircularProgress size={16} />}
          >
            {t('SHARED_SAVE')}
          </Button>
        </div>
      )}
    </div>
  );
}
